import { Popover as HeadlessPopover } from '@headlessui/react'
import type { PropsWithChildren } from 'react'
import { useState } from 'react'
import { usePopper } from 'react-popper'
import { classNames as cn } from '~/helpers/string'

export type PopoverProps = PropsWithChildren<{
  trigger: React.ReactNode
  enabled?: boolean
  placement?:
    | 'auto'
    | 'auto-end'
    | 'auto-start'
    | 'bottom'
    | 'bottom-end'
    | 'bottom-start'
    | 'left'
    | 'left-end'
    | 'left-start'
    | 'right'
    | 'right-end'
    | 'right-start'
    | 'top'
    | 'top-end'
    | 'top-start'
  strategy?: 'absolute' | 'fixed'
  className?: string
}>

export function Popover(props: PopoverProps) {
  const { trigger, children, enabled = true, placement, strategy, className = '' } = props
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 15],
        },
      },
      {
        name: 'arrow',
        options: {
          element: arrowElement,
        },
      },
    ],
    placement,
    strategy,
  })

  if (!enabled) return trigger

  return (
    <HeadlessPopover as="div" className="relative">
      <HeadlessPopover.Button ref={setReferenceElement} className="outline-none">
        {trigger}
      </HeadlessPopover.Button>

      <HeadlessPopover.Panel
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
        className="group z-10 rounded-lg bg-brand-white shadow-[0_0_12px_0_rgba(0,0,0,0.2)]"
      >
        <div className={cn('px-5 py-2.5', className)}>{children}</div>
        <div
          ref={setArrowElement}
          style={styles.arrow}
          className={cn(
            'invisible',
            'absolute h-[14px] w-[14px] rounded-[0.125rem] bg-inherit',
            'before:absolute before:h-[14px] before:w-[14px] before:rounded-[0.125rem] before:bg-inherit',
            'before:visible before:rotate-45 before:content-[""]',
            'group-data-[popper-placement^=top]:bottom-[-6px]',
            'group-data-[popper-placement^=bottom]:top-[-6px]',
            'group-data-[popper-placement^=left]:right-[-6px]',
            'group-data-[popper-placement^=right]:left-[-6px]',
          )}
        />
      </HeadlessPopover.Panel>
    </HeadlessPopover>
  )
}
